<template>
  <div class="p-6 max-w-2xl">
    <div class="mb-6 text-center">
      <div class="inline-flex justify-center items-center p-3 mb-4 bg-blue-100 rounded-full">
        <IconPermissions class="w-8 h-8 text-blue-600" />
      </div>
      <h2 class="mb-2 text-xl font-bold text-gray-900 dark:text-gray-100">
        🔐 Sécurisation de votre compte
      </h2>
      <p class="text-sm text-gray-600 dark:text-gray-400">
        Pour sécuriser votre compte et permettre la récupération d'urgence,
        veuillez configurer au moins une question de sécurité.
      </p>
    </div>

    <div v-if="!isCompleted">
      <!-- Loading State -->
      <div v-if="isLoadingQuestions" class="py-8 text-center">
        <ProgressSpinner style="width: 50px; height: 50px" stroke-width="4" />
        <p class="mt-4 text-sm text-gray-600">Chargement des questions...</p>
      </div>

      <!-- Main Form -->
      <div v-else>
        <div class="mb-6">
          <h3 class="mb-4 font-medium text-gray-900 dark:text-gray-100">
            Choisissez vos questions de sécurité
          </h3>
          <SecurityQuestionsForm 
            ref="formRef"
            :is-first-time-setup="true"
            @submit="saveQuestions"
            @skip="skipSetup" 
          />
        </div>
      </div>
    </div>

    <!-- Completion Message -->
    <div v-else class="text-center">
      <div class="inline-flex justify-center items-center p-3 mb-4 bg-green-100 rounded-full">
        <Icon name="check-circle" class="w-8 h-8 text-green-600" />
      </div>
      <h3 class="mb-2 text-lg font-medium text-gray-900 dark:text-gray-100">
        ✅ Configuration terminée !
      </h3>
      <p class="mb-6 text-sm text-gray-600 dark:text-gray-400">
        Vos questions de sécurité ont été configurées avec succès.
      </p>
      <Button severity="primary" @click="closeModal"> Continuer </Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import IconPermissions from "~/assets/icons/shield-check.svg";
import SecurityQuestionsForm from "./SecurityQuestionsForm.vue";
import type { SecurityQuestionsForm as SecurityQuestionsFormType } from "~/types/SecurityQuestionsValidation";

const dialogRef = inject("dialogRef");

// Reactive data
const apiFetch = useSanctumClient();
const isCompleted = ref(false);
const formRef = ref<InstanceType<typeof SecurityQuestionsForm>>();

// Methods
async function saveQuestions(formData: SecurityQuestionsFormType) {
  try {
    formRef.value?.setSaving(true);
    formRef.value?.setStatusMessage({ level: "", message: "" });

    const questions = formData.questions.map((q) => {
      const questionText = q.question === "Question personnalisée" ? q.customQuestion : q.question;
      return {
        question: questionText,
        answer: q.answer.trim(),
      };
    });

    await apiFetch("/api/user/security-questions", {
      method: "POST",
      body: { questions },
    });

    isCompleted.value = true;

    // Auto-close after 2 seconds
    setTimeout(() => {
      closeModal();
    }, 2000);
  } catch (error: any) {
    console.error("Error saving security questions:", error);
    formRef.value?.setStatusMessage({
      level: "error",
      message: error.data?.message || "Erreur lors de l'enregistrement des questions de sécurité",
    });
  } finally {
    formRef.value?.setSaving(false);
  }
}

function skipSetup() {
  dialogRef?.value?.close({ action: "skipped" });
}

function closeModal() {
  dialogRef?.value?.close({ action: "completed" });
}
</script>
